import React, { useEffect, useMemo } from "react";
import Select from "react-select";
import { useQuery } from "@tanstack/react-query";
import { fetchCityByStateID } from "services/dropdown";
import { DROPDOWN_STALE_TIME } from "utils/constants";
import { Controller } from "react-hook-form";

const CityDropdown = ({
  errors,
  control,
  setValue,
  watch,
  rowData,
  required,
  stateID,
}) => {
  const selectedCity = watch("city_id");

  const { data, isLoading, error } = useQuery(
    ["city-dropdown", stateID],
    () => fetchCityByStateID(stateID),
    {
      enabled: !!stateID,
      keepPreviousData: false,
      staleTime: DROPDOWN_STALE_TIME,
    },
  );

  const cityList = useMemo(() => {
    const responseData = data?.data;

    if (Array.isArray(responseData)) return responseData;
    if (Array.isArray(responseData?.data)) return responseData.data;
    if (Array.isArray(responseData?.city_data)) return responseData.city_data;
    if (Array.isArray(responseData?.cities)) return responseData.cities;

    return [];
  }, [data]);

  const options = useMemo(
    () =>
      cityList.map((item) => ({
        value: item.city_id,
        label: item.city,
      })) || [],
    [cityList],
  );

  useEffect(() => {
    if (rowData && options.length) {
      let selectedOption = options.find(
        (option) => option.value === rowData?.city_id,
      );

      if (!selectedOption) {
        selectedOption = options.find(
          (option) => option.label === rowData?.city,
        );
      }

      if (selectedOption) {
        setValue("city_id", selectedOption.value);
        setValue("city", selectedOption.label);
      }
    }
  }, [rowData, options, setValue]);

  if (!stateID) return <div>Select State first</div>;
  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading City</div>;

  return (
    <Controller
      name="city_id"
      control={control}
      rules={{
        required: required ? "City is required" : false,
        validate: (value) => {
          if (required && !value) return "City is required";
          return true;
        },
      }}
      render={({ field }) => {
        const selectedOption = options.find(
          (option) => option.value === selectedCity,
        );

        return (
          <Select
            {...field}
            options={options}
            isClearable
            placeholder="Select City"
            menuPortalTarget={document.body}
            value={selectedOption || null}
            onChange={(selectedOption) => {
              const value = selectedOption ? selectedOption.value : "";
              const label = selectedOption ? selectedOption.label : "";
              field.onChange(value);
              setValue("city_id", value);
              setValue("city", label);
            }}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
            className={`react-select-container ${
              errors.city_id ? "is-invalid" : ""
            }`}
          />
        );
      }}
    />
  );
};

export default CityDropdown;
